import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';

import DefaultSelectRender, {
  Props as DefaultProps,
} from './DefaultSelectRender';

import styles from './SelectFieldCustom.module.scss';

export type Props = {
  items: ItemType[];
  data: string | undefined;
  setData: (value: string) => void;
  placeholder?: string;
  classes?: Partial<SelectFieldCustomClasses>;
  disabled?: boolean;
  onSelectOpen?: () => void;
  RenderComponent?: React.ElementType<DefaultProps<Option>>;
  [key: string]: unknown;
};

function SelectFieldCustom({
  items,
  data,
  setData,
  placeholder,
  classes,
  disabled = false,
  onSelectOpen,
  RenderComponent,
  ...props
}: Props): JSX.Element {
  const Component = RenderComponent || DefaultSelectRender;
  const [open, setOpen] = useState<boolean>(false);
  const options = useMemo(() => {
    const list = items.map<Option>(item => ({
      value: item.value,
      label: item.content as string,
      isDisabled: item.disabled || false,
    }));

    return [
      {
        value: undefined,
        label: (
          <>
            <span>{placeholder}</span>
            <CloseIcon
              className={styles.closeIcon}
              onClick={() => setOpen(false)}
            />
          </>
        ),
        isPlaceholder: true,
        isDisabled: true,
        classNames: {
          item: styles.popoverPlaceholder,
        },
      },
      ...list,
    ];
  }, [items, placeholder]);

  const selected = useMemo(() => {
    return data ? options.find(option => option.value === data) : undefined;
  }, [options, data]);

  useEffect(() => {
    if (open) onSelectOpen?.();
  }, [open]);

  return (
    <Component
      onMenuOpen={() => setOpen(true)}
      onMenuClose={() => setOpen(false)}
      options={options}
      placeholder={placeholder}
      classes={classes}
      isDisabled={disabled}
      onChange={option => setData(option?.value || '')}
      value={selected ?? null}
      menuIsOpen={open}
      autoFocus={false}
      isSearchable={false}
      {...props}
    />
  );
}

export default React.memo(SelectFieldCustom);

export type ItemType = {
  content: string | JSX.Element;
  value: string;
  disabled?: boolean;
};

export type SelectFieldCustomClasses = {
  openedContainer?: string;
  menu?: string;
  menuList?: string;
  item?: string;
};

export type Option = {
  value?: string;
  label: string | JSX.Element;
  isPlaceholder?: boolean;
  isDisabled?: boolean;
  classNames?: Partial<{
    item?: string;
  }>;
};
